import api from "./api";
import Vue from "vue";
import { cloneDeep, isEqual } from "lodash";
import { mapQuoteResponseToFormData, mapFormDataToBody } from "@/modules/quotes/components/mapper";
import {
  getQuoteValidationIssues,
  formatQuoteValidationMessage,
} from "@/modules/quotes/components/quoteValidation";

function unwrapApiError(err) {
  if (err == null || err === "") return "Request failed";
  if (typeof err === "string") return err;
  const payload = err.data !== undefined && err.data !== null && err.data !== "" ? err.data : err;
  if (typeof payload === "string" && payload) return payload;
  if (payload && typeof payload === "object") {
    return payload.sqlMessage || payload.message || payload.error || err.message || "Request failed";
  }
  return err.message || String(err);
}

/** Avoid writing large HTML/base64 blobs to localStorage (blocks the UI for many seconds). */
function leanQuoteForStorage(quote) {
  if (!quote || typeof quote !== "object") return quote;
  const extraPages = quote.program?.extraPages;
  if (!Array.isArray(extraPages) || !extraPages.some((page) => page && page.mode === "detached" && page.content)) {
    return quote;
  }
  return {
    ...quote,
    program: {
      ...quote.program,
      extraPages: extraPages.map((page) => {
        if (!page || page.mode !== "detached") return page;
        return {
          ...page,
          content: { html: "" },
        };
      }),
    },
  };
}

/** Fingerprint large HTML so dirty-checks don't keep a second full copy in RAM. */
function fingerprintHtml(html) {
  const s = String(html || "");
  return `${s.length}:${s.slice(0, 48)}:${s.slice(-48)}`;
}

function leanQuoteForCompare(quote) {
  if (!quote || typeof quote !== "object") return quote;
  const extraPages = quote.program?.extraPages;
  if (!Array.isArray(extraPages) || !extraPages.some((page) => page && page.mode === "detached" && page.content?.html)) {
    return quote;
  }
  return {
    ...quote,
    program: {
      ...quote.program,
      extraPages: extraPages.map((page) => {
        if (!page || page.mode !== "detached" || !page.content) return page;
        return {
          ...page,
          content: {
            ...page.content,
            html: fingerprintHtml(page.content.html),
          },
        };
      }),
    },
  };
}

const namespaced = true;

const SET_QUOTE = "SET_QUOTE";
const TRANSFER_QUOTE = "TRANSFER_QUOTE";
const UPDATE_QUOTE = "UPDATE_QUOTE";
const SET_SUPPLIER_PRODUCT_DATA = "SET_SUPPLIER_PRODUCT_DATA";
const SET_ROUTES = "SET_ROUTES";
const SET_IMAGES = "SET_IMAGES";
const SET_PROGRAM_PAGES = "SET_PROGRAM_PAGES";
const CLEAR_QUOTE_SNAPSHOT = "CLEAR_QUOTE_SNAPSHOT";

const state = {
  active: _getQuote(),
  cache: _getQuote(),
  savedQuoteSnapshot: null,
  validationIssues: [],
  supplierProductData: _getSupplierProductData(),
  routes: _getRoutes(),
  images: _getImages(),
  programPages: _getProgramPages(),
};

function _getQuote() {
  const quote = localStorage.getItem("quote");
  if (!quote || quote === "{}") return null;
  return JSON.parse(quote);
}

function _getSupplierProductData() {
  const supplierProductData = localStorage.getItem("supplier_product_data");
  if (!supplierProductData || supplierProductData === "{}") return null;
  return JSON.parse(supplierProductData);
}

function _getRoutes() {
  const routes = localStorage.getItem("routes");
  if (!routes || routes === "[]") return null;
  return JSON.parse(routes);
}

function _getImages() {
  const images = localStorage.getItem("images");
  if (!images || images === "[]") return null;
  return JSON.parse(images);
}

function _getProgramPages() {
  // Full page HTML is kept in memory only (localStorage would freeze the UI).
  return [];
}

const mutations = {
  SET_QUOTE(state, data) {
    state.active = data || {};
    state.cache = { ...state.active };
    localStorage.setItem("quote", JSON.stringify(leanQuoteForStorage(state.active)));
  },
  SAVE_QUOTE_SNAPSHOT(state) {
    state.savedQuoteSnapshot = state.active ? cloneDeep(leanQuoteForCompare(state.active)) : null;
  },
  CLEAR_QUOTE_SNAPSHOT(state) {
    state.savedQuoteSnapshot = null;
  },
  SET_VALIDATION_ISSUES(state, issues) {
    state.validationIssues = issues || [];
  },
  TRANSFER_QUOTE(state) {
    // Transfer active quote => cache
    // Save update local storage
    localStorage.setItem("quote", JSON.stringify(leanQuoteForStorage(state.active)));
  },
  UPDATE_QUOTE(state, data) {
    Vue.set(state.active, data.key, data.value);
  },
  SET_SUPPLIER_PRODUCT_DATA(state, data) {
    state.supplierProductData = data;
    localStorage.setItem("supplier_product_data", JSON.stringify(state.supplierProductData));
  },
  SET_ROUTES(state, routes) {
    state.routes = routes;
    localStorage.setItem("routes", JSON.stringify(routes));
  },
  SET_IMAGES(state, images) {
    state.images = images;
    localStorage.setItem("images", JSON.stringify(images));
  },
  SET_PROGRAM_PAGES(state, programPages) {
    state.programPages = programPages || [];
    // Never persist HTML content — base64 images make JSON.stringify multi-second.
    try {
      localStorage.removeItem("program_pages");
    } catch (e) {
      // ignore quota / private mode
    }
  },
};

const actions = {
  getQuoteList({ commit }, query) {
    return api
      .getQuoteList(query)
      .then((data) => data.data)
      .catch((err) => {
        throw unwrapApiError(err);
      });
  },
  getOneQuote({ commit }, quote_id) {
    return api
      .getOneQuote(quote_id)
      .then((data) => {
        const supplierProductData = {};
        data.data.suppliers.forEach((supplier) => {
          Vue.set(supplierProductData, supplier.id, { supplier, products: {} });
        });
        data.data.products.forEach((product) => {
          if (!supplierProductData[product.supplier_id]) {
            Vue.set(supplierProductData, product.supplier_id, {
              supplier: { id: product.supplier_id, name: "", meta: {}, city: null },
              products: {},
            });
          }
          Vue.set(supplierProductData[product.supplier_id].products, product.id, product);
        });

        const quote = mapQuoteResponseToFormData(supplierProductData, data.data);

        commit(SET_QUOTE, quote);
        commit(SET_SUPPLIER_PRODUCT_DATA, supplierProductData);
        commit(SET_ROUTES, data.data.routes);
        commit(SET_IMAGES, data.data.images || []);
        commit(SET_PROGRAM_PAGES, data.data.program_pages || []);

        return data.data;
      })
      .catch((err) => {
        throw unwrapApiError(err);
      });
  },
  update({ commit }, { key, value }) {
    commit(UPDATE_QUOTE, { key, value });
  },
  updateQuote({ commit, state }, environment) {
    if (!state.active) return Promise.resolve();
    const quote_id = state.active.id;
    const workingEnv = typeof environment === "string" && environment ? environment : state.active.environment;
    const data = mapFormDataToBody(state.active, workingEnv);

    if (!quote_id) return Promise.resolve();

    return api
      .updateQuote(quote_id, data)
      .then((v) => {
        // Update new booking
        commit(TRANSFER_QUOTE);
      })
      .catch((err) => {
        if (err) throw unwrapApiError(err);
      });
  },
  saveActiveQuote({ commit, dispatch, state, rootGetters }) {
    if (!state.active?.id) {
      return Promise.reject("No quote to save");
    }

    const validationIssues = getQuoteValidationIssues(state.active);
    if (validationIssues.length) {
      commit("SET_VALIDATION_ISSUES", validationIssues);
      return Promise.reject(formatQuoteValidationMessage(validationIssues));
    }

    const environment = state.active.environment || rootGetters["AccountStore/environment"];
    return dispatch("updateQuote", environment).then(() => {
      commit("SAVE_QUOTE_SNAPSHOT");
      commit("SET_VALIDATION_ISSUES", []);
    });
  },
  updateQuoteById({ commit }, { quote_id, data }) {
    return api.updateQuote(quote_id, data).catch((err) => {
      if (err) throw unwrapApiError(err);
    });
  },
  patchQuote({ commit, state }, { quote_id, quote }) {
    return api
      .updateQuote(quote_id, { quote })
      .then((v) => {
        commit(TRANSFER_QUOTE);
      })
      .catch((err) => {
        if (err) throw unwrapApiError(err);
      });
  },
  createQuote({ commit }, data) {
    return api
      .createQuote(data)
      .then((data) => {
        return data.data;
      })
      .catch((err) => {
        throw unwrapApiError(err);
      });
  },
  convertQuoteToBooking({ commit }, quote_id) {
    return api
      .convertQuoteToBooking(quote_id)
      .then((data) => {
        return data.data;
      })
      .catch((err) => {
        throw unwrapApiError(err);
      });
  },
  deleteQuote({ commit }, quote_id) {
    return api
      .deleteQuote(quote_id)
      .then((data) => data.data)
      .catch((err) => {
        throw unwrapApiError(err);
      });
  },
  getDocumentList(v, supplier_id) {
    return api
      .getDocumentList(supplier_id)
      .then((data) => data.data)
      .catch((err) => {
        if (err) throw err.data;
      });
  },
  addDocument(v, { origin_id, name, key }) {
    return api
      .addDocument(origin_id, { name, key })
      .then((data) => data.data)
      .catch((err) => {
        if (err) throw err.data;
      });
  },
  updateDocument(v, { id, display_name, old_name, dependent_id }) {
    return api
      .updateDocument(id, {
        display_name,
        old_name,
        dependent_id,
      })
      .then((data) => data.data)
      .catch((err) => {
        if (err) throw err.data;
      });
  },
  deleteDocumentList(v, idList) {
    return api
      .deleteDocumentList(idList)
      .then((data) => data.data)
      .catch((err) => {
        if (err) throw err.data;
      });
  },
  getProductsBySupplierId({ state, commit, dispatch }, { supplierId, productType }) {
    return dispatch("ProductStore/getSupplierProductList", supplierId, { root: true })
      .then((data) => {
        let productsToProcess = [...data];
        if (productType) {
          productsToProcess.sort((a, b) => {
            const aIsMatch = a.product_type === productType;
            const bIsMatch = b.product_type === productType;
            return Number(bIsMatch) - Number(aIsMatch);
          });
        }
        const products = {};
        for (const product of productsToProcess) {
          products[product.id] = product;
        }
        const updatedSupplierProductData = { ...state.supplierProductData };
        updatedSupplierProductData[supplierId] = {
          ...updatedSupplierProductData[supplierId],
          products: products,
        };
        commit("SET_SUPPLIER_PRODUCT_DATA", updatedSupplierProductData);
      })
      .catch((error) => {
        console.error("Error fetching supplier product list:", error);
      });
  },
};

const getters = {
  active: (state) => state.active,
  hasUnsavedChanges: (state) => {
    if (!state.active || !state.savedQuoteSnapshot) return false;
    return !isEqual(leanQuoteForCompare(state.active), state.savedQuoteSnapshot);
  },
  validationIssues: (state) => state.validationIssues,
  quote_id: (state) => state.active?.id,
  where_start: (state) => state.active?.cityStart,
  where_end: (state) => state.active?.cityEnd,
  nights: (state) => state.active?.nights,
  product_price_month: (state) => state.active?.period?.startMonth,
  product_price_year: (state) => state.active?.period?.startYear,
  supplierProductData: (state) => state.supplierProductData,
  routes: (state) => state.routes,
  images: (state) => state.images,
  programPages: (state) => state.programPages,
};

export default {
  namespaced,
  state,
  mutations,
  actions,
  getters,
};
